import { HTTPError } from "ky";

export type BillingInterval = "MONTHLY" | "YEARLY";
export type SubscriptionStatus =
  | "TRIALING"
  | "ACTIVE"
  | "PAST_DUE"
  | "LOCKED"
  | "CANCELED";

export type PlanResponse = {
  code: string;
  name: string;
  max_seats: number | null;
  included_minutes: number | null;
  is_self_serve: boolean;
};

export type EnterpriseCompanySize =
  | "SIZE_1_50"
  | "SIZE_51_200"
  | "SIZE_201_1000"
  | "SIZE_1001_5000"
  | "SIZE_5000_PLUS";

export type EnterpriseExpectedVolume =
  | "MINUTES_UNDER_25K"
  | "MINUTES_25K_PLUS"
  | "MINUTES_50K_PLUS"
  | "MINUTES_100K_PLUS"
  | "NOT_SURE";

export type EnterpriseInquiryRequest = {
  full_name: string;
  email: string;
  company: string;
  company_size: EnterpriseCompanySize;
  expected_volume: EnterpriseExpectedVolume;
  notes: string;
};

export type EnterpriseInquiryResponse = {
  message: string;
};

export type QuoteRequest = {
  seats: number;
  minutes: number;
  interval: BillingInterval;
};

export type QuoteLine = {
  label: string;
  quantity: number;
  unit_cents: number;
  subtotal_cents: number;
};

export type QuoteResponse = QuoteRequest & {
  requested_minutes: number;
  minutes_normalised: boolean;
  total_cents: number;
  monthly_total_cents: number;
  yearly_total_cents: number;
  lines: Array<QuoteLine>;
};

export type SubscriptionResponse = {
  org_id: string;
  plan_code: string;
  plan_name: string;
  status: SubscriptionStatus;
  billing_interval: BillingInterval;
  is_grandfathered: boolean;
  stripe_billed: boolean;
  seats_purchased: number;
  minutes_purchased: number;
  seat_limit_override: number | null;
  minute_limit_override: number | null;
  seat_limit: number | null;
  minute_limit: number | null;
  enforce_limits: boolean;
  current_period_start: string;
  current_period_end: string;
  suspend_at: string | null;
  locked_at: string | null;
};

export function isCurrentSubscriptionPlan(
  subscription: Pick<SubscriptionResponse, "plan_code" | "status"> | undefined,
  planCode: string | undefined,
) {
  return Boolean(
    subscription &&
    planCode &&
    subscription.status !== "CANCELED" &&
    subscription.plan_code === planCode,
  );
}

export type CheckoutSessionRequest = QuoteRequest & {
  success_url: string;
  cancel_url: string;
};

export type CheckoutSessionResponse = {
  url: string;
  minutes: number;
  seats: number;
  interval: BillingInterval;
  total_cents: number;
};

export type PortalSessionRequest = { return_url: string };
export type PortalSessionResponse = { url: string };
export type IntervalChangeRequest = { interval: BillingInterval };

export type IntervalPreviewResponse = {
  interval: BillingInterval;
  amount_due_now_cents: number;
  proration_credit_cents: number;
  new_total_cents: number;
  effective_at: string;
  is_immediate: boolean;
};

export type IntervalChangeResponse = SubscriptionResponse & {
  pending_interval: BillingInterval | null;
  effective_at: string;
};

export type MinuteDisplay = {
  minutes: number;
  hours: number;
  remainder_minutes: number;
  label: string;
};

export type UsageResponse = {
  minutes_included: number | null;
  minutes_used: number;
  minutes_reserved: number;
  minutes_remaining: number | null;
  billing_interval: BillingInterval;
  display: {
    used: MinuteDisplay;
    reserved: MinuteDisplay;
    remaining: MinuteDisplay | null;
  };
  period_start: string;
  period_end: string;
  notified_thresholds: Array<number>;
};

export type UsageEventResponse = {
  id: string;
  meeting_bot_id: string;
  meeting_bot_external_id: string;
  created_by_user_id: string;
  billing_period_id: string;
  billable_minutes: number;
  raw_duration_ms: number;
  meeting_started_at: string | null;
  meeting_ended_at: string;
  created_at: string;
  billable_display: MinuteDisplay;
};

export type PaginatedResponse<T> = {
  count: number;
  items: Array<T>;
  next_page?: string | null;
  previous_page?: string | null;
};

export type BillingErrorCode =
  | "QUOTA_EXCEEDED"
  | "SUBSCRIPTION_SUSPENDED"
  | "PAYMENT_SERVICE_UNAVAILABLE"
  | "SEAT_LIMIT_REACHED"
  | "ORGANIZATION_LOCKED"
  | "UPGRADE_PAYMENT_FAILED"
  | "BILLING_NOT_CONFIGURED";

export type BillingErrorDetail = {
  code: BillingErrorCode;
  message: string;
  minutes_remaining?: number | null;
  period_end?: string | null;
  seat_limit?: number | null;
  seats_used?: number | null;
  upgrade_url?: string;
  billing_url?: string;
};

export const BILLING_CHECKOUT_EXPECTATION_KEY = "billing-checkout-expectation";

export type CheckoutExpectation = Pick<
  CheckoutSessionResponse,
  "minutes" | "seats" | "interval"
>;

const BILLING_ERROR_CODES = new Set<BillingErrorCode>([
  "QUOTA_EXCEEDED",
  "SUBSCRIPTION_SUSPENDED",
  "PAYMENT_SERVICE_UNAVAILABLE",
  "SEAT_LIMIT_REACHED",
  "ORGANIZATION_LOCKED",
  "UPGRADE_PAYMENT_FAILED",
  "BILLING_NOT_CONFIGURED",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isBillingInterval(value: unknown): value is BillingInterval {
  return value === "MONTHLY" || value === "YEARLY";
}

function isBillingErrorCode(value: unknown): value is BillingErrorCode {
  return (
    typeof value === "string" &&
    BILLING_ERROR_CODES.has(value as BillingErrorCode)
  );
}

function parseBillingErrorDetail(value: unknown): BillingErrorDetail | null {
  if (!isRecord(value)) return null;
  const { code, message } = value;
  if (!isBillingErrorCode(code) || typeof message !== "string") {
    return null;
  }

  const detail: BillingErrorDetail = { code, message };
  if (
    typeof value.minutes_remaining === "number" ||
    value.minutes_remaining === null
  ) {
    detail.minutes_remaining = value.minutes_remaining;
  }
  if (typeof value.period_end === "string" || value.period_end === null) {
    detail.period_end = value.period_end;
  }
  if (typeof value.seat_limit === "number" || value.seat_limit === null) {
    detail.seat_limit = value.seat_limit;
  }
  if (typeof value.seats_used === "number" || value.seats_used === null) {
    detail.seats_used = value.seats_used;
  }
  if (typeof value.upgrade_url === "string") {
    detail.upgrade_url = value.upgrade_url;
  }
  if (typeof value.billing_url === "string") {
    detail.billing_url = value.billing_url;
  }
  return detail;
}

async function readJson(response: Response): Promise<unknown> {
  return response
    .clone()
    .json()
    .catch(() => null);
}

export async function readBillingErrorResponse(
  response: Response,
): Promise<BillingErrorDetail | null> {
  if (response.status !== 402 && response.status !== 503) return null;
  const body = await readJson(response);
  return parseBillingErrorDetail(isRecord(body) ? body.detail : null);
}

export async function asBillingError(
  error: unknown,
): Promise<BillingErrorDetail | null> {
  if (!(error instanceof HTTPError)) return null;
  return readBillingErrorResponse(error.response);
}

export async function readApiErrorMessage(
  error: unknown,
): Promise<string | null> {
  if (!(error instanceof HTTPError)) return null;
  const body = await readJson(error.response);
  if (!isRecord(body)) return null;
  if (typeof body.detail === "string") return body.detail;
  if (!Array.isArray(body.detail)) return null;

  const messages = body.detail.flatMap((item) =>
    isRecord(item) && typeof item.msg === "string" ? [item.msg] : [],
  );
  return messages.length > 0 ? messages.join(" ") : null;
}

export function parseCheckoutExpectation(
  value: string | null,
): CheckoutExpectation | null {
  if (!value) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (
      !isRecord(parsed) ||
      typeof parsed.minutes !== "number" ||
      !Number.isInteger(parsed.minutes) ||
      parsed.minutes < 0 ||
      typeof parsed.seats !== "number" ||
      !Number.isInteger(parsed.seats) ||
      parsed.seats < 0 ||
      !isBillingInterval(parsed.interval)
    ) {
      return null;
    }
    return {
      minutes: parsed.minutes,
      seats: parsed.seats,
      interval: parsed.interval,
    };
  } catch {
    return null;
  }
}

export function getPendingIntervalChange(subscription: SubscriptionResponse) {
  const candidate = subscription as SubscriptionResponse &
    Partial<IntervalChangeResponse>;
  if (
    !isBillingInterval(candidate.pending_interval) ||
    typeof candidate.effective_at !== "string"
  ) {
    return null;
  }
  return {
    interval: candidate.pending_interval,
    effectiveAt: candidate.effective_at,
  };
}

export const MIN_BUSINESS_MINUTES = 1_000;
export const MAX_BUSINESS_MINUTES = 100_000;
export const MINUTE_INCREMENT = 60;
export const MAX_BUSINESS_SEATS = 50;
